import type { MMU } from '../mmu/MMU';
import type { ModelConfig } from '../../model/ModelConfig';

export interface Peripheral {
  readonly name: string;
  readonly baseAddress: number;
  readonly regionSize: number;

  init(mmu: MMU, modelConfig: ModelConfig): void;
  reset(): void;
  destroy(): void;

  tick(): void;
  tickAfterInterrupts(): void;

  readRegister(offset: number): number;
  writeRegister(offset: number, value: number): void;

  saveSnapshot(): PeripheralSnapshot;
  loadSnapshot(snapshot: PeripheralSnapshot): void;
}

export interface PeripheralSnapshot {
  name: string;
  data: Record<string, unknown>;
}

export class PeripheralManager {
  private peripherals: Map<string, Peripheral> = new Map();

  constructor() {}

  register(peripheral: Peripheral): void {
    this.peripherals.set(peripheral.name, peripheral);
  }

  unregister(name: string): void {
    this.peripherals.delete(name);
  }

  getPeripheral(name: string): Peripheral | null {
    return this.peripherals.get(name) ?? null;
  }

  tickAll(): void {
    for (const peripheral of this.peripherals.values()) {
      peripheral.tick();
    }
  }

  tickAfterInterrupts(): void {
    for (const peripheral of this.peripherals.values()) {
      peripheral.tickAfterInterrupts();
    }
  }

  initAll(mmu: MMU, modelConfig: ModelConfig): void {
    for (const peripheral of this.peripherals.values()) {
      peripheral.init(mmu, modelConfig);
    }
  }

  resetAll(): void {
    for (const peripheral of this.peripherals.values()) {
      peripheral.reset();
    }
  }

  destroyAll(): void {
    for (const peripheral of this.peripherals.values()) {
      peripheral.destroy();
    }
  }

  saveSnapshots(): PeripheralSnapshot[] {
    return Array.from(this.peripherals.values()).map(p => p.saveSnapshot());
  }

  loadSnapshots(snapshots: PeripheralSnapshot[]): void {
    for (const snap of snapshots) {
      const peripheral = this.peripherals.get(snap.name);
      if (peripheral) {
        peripheral.loadSnapshot(snap);
      }
    }
  }
}
